package com.bikompanion.ui.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.zacsweers.metro.ContributesIntoMap
import dev.zacsweers.metro.Inject
import dev.zacsweers.metrox.viewmodel.ViewModelKey
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.bikompanion.domain.BicingNetworkId
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeNetworkRepository
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BikeRatingRepository
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.LanguagePreference
import com.bikompanion.domain.LanguagePreferencesRepository
import com.bikompanion.domain.MapLayerMode
import com.bikompanion.domain.MapLayerPreferencesRepository
import com.bikompanion.domain.ParkingCell
import com.bikompanion.domain.RateLimitedException
import com.bikompanion.domain.StartPosition
import com.bikompanion.domain.StationWatch
import com.bikompanion.domain.ThemeMode
import com.bikompanion.domain.ThemePreferencesRepository
import com.bikompanion.domain.analytics.AnalyticsTracker
import com.bikompanion.domain.analytics.analyticsValue
import com.bikompanion.domain.catchingRequest
import com.bikompanion.domain.di.AppScope
import com.bikompanion.domain.usecase.FindChangedStationsUseCase
import com.bikompanion.domain.usecase.GuessStartPositionUseCase
import com.bikompanion.ui.analytics.AnalyticsEvents
import com.bikompanion.ui.composables.STATIONS_VISIBLE_ZOOM
import com.bikompanion.ui.models.BikeRatingUiState
import com.bikompanion.ui.models.DataFreshness
import com.bikompanion.ui.models.LoadOutcome
import com.bikompanion.ui.models.ParkingAreaSummary
import com.bikompanion.ui.models.ParkingCountdownStatus
import com.bikompanion.ui.models.RefreshStatus
import com.bikompanion.ui.settings.SettingsPage
import com.bikompanion.ui.strings.preferredLanguageTag
import com.bikompanion.ui.watch.StationNotificationTarget
import com.bikompanion.ui.watch.StationWatchEvent
import kotlin.time.ComparableTimeMark
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/**
 * Below this zoom, the periodic refresh loop stops re-fetching networks it has already loaded.
 *
 * A network newly panned into view is always fetched at least once regardless of zoom — see
 * [HomeViewModel.loadStations] — so positions and last-known availability still appear at any
 * zoom level. What this threshold controls is only the *repeat* fetch: below it, a wide view can
 * hold two dozen networks at once, and polling all of them every [RefreshInterval] costs far more
 * than the zoomed-out numbers are worth looking at closely. Chosen to match [refreshBudgetAt]'s
 * `>= 13.0` tier, the point past which the budget itself starts allowing a lot of networks in at
 * once.
 */
internal const val RefreshZoomThreshold = 13.0

/**
 * How many of the networks in view are worth polling, given the zoom.
 *
 * A flat cap made wide views lie: the viewport can straddle twenty-plus networks, and every
 * one past the cap sat frozen at whatever it held when it first scrolled past. The budget
 * grows as the map pulls back to keep most of what's drawn actually live.
 *
 * Each network is its own request, so the top of the range is the ceiling on what one refresh
 * cycle costs. Only one network exists under the GBFS source, so this currently costs
 * nothing; it matters again if CityBikes comes back.
 */
private fun refreshBudgetAt(zoom: Double): Int =
    when {
        // Street level sits inside one operator's patch; the rest covers the edges.
        zoom >= 15.0 -> 4

        zoom >= 13.0 -> 10

        zoom >= 12.0 -> 18

        else -> 28
    }

private val OneSecond = 1.seconds

private fun Map<String, List<BikeStation>>.upsert(
    networkId: String,
    station: BikeStation,
): Map<String, List<BikeStation>> {
    val current = this[networkId].orEmpty()
    val stationIndex = current.indexOfFirst { it.id == station.id }
    val updated = if (stationIndex < 0) {
        current + station
    } else {
        current.toMutableList().apply {
            this[stationIndex] = station
        }
    }
    return this + (networkId to updated)
}

/**
 * Owns the home screen's lifecycle and what is live on the map: networks, stations and the
 * refresh loop. Rating, racks, the pickup countdown and watches are in their own helpers, which
 * share this model's [HomeSession].
 */
@Inject
@ViewModelKey
@ContributesIntoMap(AppScope::class)
class HomeViewModel(
    private val networkRepository: BikeNetworkRepository,
    private val themePreferences: ThemePreferencesRepository,
    private val languagePreferences: LanguagePreferencesRepository,
    private val mapLayerPreferences: MapLayerPreferencesRepository,
    ratingRepository: BikeRatingRepository,
    private val findChangedStations: FindChangedStationsUseCase,
    private val guessStartPosition: GuessStartPositionUseCase,
    private val analytics: AnalyticsTracker,
    parkingCountdownFactory: ParkingCountdown.Factory,
    ratingFactory: BikeRatingHandler.Factory,
    parkingRackFactory: ParkingRackLoader.Factory,
    stationWatchFactory: StationWatchTracker.Factory,
) : ViewModel() {
    // Read synchronously so the first frame is already in the right scheme, not a flash of the
    // system one before a stored override lands.
    private val _stateFlow = MutableStateFlow(
        State(
            themeMode = themePreferences.getThemeMode(),
            language = languagePreferences.getLanguagePreference(),
            mapLayerMode = mapLayerPreferences.getMapLayerMode(),
            // Before the networks load there is only the time zone's city to go on.
            startPosition = guessStartPosition(networks = emptyList()),
        ),
    )
    val stateFlow: StateFlow<State> = _stateFlow.asStateFlow()

    private val session = HomeSession(viewModelScope, _stateFlow)
    private val scope = session.scope
    private val parkingCountdown = parkingCountdownFactory.create(session)
    private val rating = ratingFactory.create(session, parkingCountdown)
    private val parkingRacks = parkingRackFactory.create(session)
    private val stationWatches = stationWatchFactory.create(session)

    /** The networks drawn on screen right now — the only ones worth keeping fresh. */
    private var visibleNetworkIds: List<String> = emptyList()

    /** The camera's current zoom, so the periodic refresh loop knows whether to poll or wait. */
    private var currentZoom: Double = 0.0

    // Bookkeeping the UI never reads, so plain collections rather than published state.
    private val inFlight = mutableSetOf<String>()
    private val loadedAt = mutableMapOf<String, TimeMark>()

    private var refreshJob: Job? = null
    private val pendingVoteRecovery = PendingVoteRecovery(scope, ratingRepository)

    /**
     * When the running countdown is due to fire next, kept across [stopAutoRefresh] calls.
     *
     * The screen stops and restarts the loop every time the app leaves and returns to the
     * foreground. Without this the countdown would reset to a fresh 30s on every such trip,
     * even a five-second one — spamming the operator's feed far more than the interval intends.
     */
    private var nextRefreshDeadline: TimeMark? = null

    init {
        preferredLanguageTag = _stateFlow.value.language.tag
        scope.launch { loadNetworks() }
        parkingCountdown.resume()
        stationWatches.publish()
        stateFlow
            .map { it.analyticsScreenName() }
            .distinctUntilChanged()
            .onEach { analytics.track(AnalyticsEvents.screenView(it)) }
            .launchIn(viewModelScope)
    }

    fun onAction(action: HomeScreenActions) {
        when (action) {
            is HomeScreenActions.StationNotificationSelected -> onStationNotificationOpened(action.target)
            is HomeScreenActions.StationLinkOpened -> onStationLinkOpened(action.target)
            is HomeScreenActions.StationSelected -> onStationSelected(action.target)
            HomeScreenActions.StationDismissed -> onStationDismissed()
            is HomeScreenActions.StationFocusHandled -> onStationFocusHandled(action.target)
            is HomeScreenActions.VisibleNetworksChanged -> onVisibleNetworksChanged(action.networkIds, action.zoom, action.visibleBounds)
            is HomeScreenActions.MapLayerModeSelected -> onMapLayerModeSelected(action.mode)
            HomeScreenActions.NetworksRetryRequested -> retryLoadNetworks()
            HomeScreenActions.ParkingRetryRequested -> retryLoadParking()
            HomeScreenActions.WatchedStationsRefreshRequested -> refreshWatchedStations()
            HomeScreenActions.AutoRefreshStarted -> startAutoRefresh()
            HomeScreenActions.AutoRefreshStopped -> stopAutoRefresh()
            is HomeScreenActions.StationWatchRequested -> watchStation(action.station, action.bikeType)
            is HomeScreenActions.StationWatchCancelled -> cancelStationWatch(action.station, action.bikeType)
            is HomeScreenActions.StationWatchesReloaded -> reloadStationWatches(action.reportExpired)
            is HomeScreenActions.StationWatchEventsConsumed -> consumeStationWatchEvents(action.count)
            HomeScreenActions.DebugFoundNotificationRequested -> requestDebugFoundNotification()
            is HomeScreenActions.ThemeModeSelected -> onThemeModeSelected(action.mode)
            is HomeScreenActions.LanguageSelected -> onLanguageSelected(action.language)
            is HomeScreenActions.SettingsPageOpened -> onSettingsPageOpened(action.page)
            HomeScreenActions.SettingsBack -> onSettingsBack()
            HomeScreenActions.SettingsClosed -> onSettingsClosed()
            HomeScreenActions.InstallPromptDismissed -> onInstallPromptDismissed()
            is HomeScreenActions.RatingBikeNumberChanged -> rating.onBikeNumberChanged(action.bikeNumber)
            is HomeScreenActions.RatingElectricToggled -> rating.onElectricToggled(action.isElectric)
            is HomeScreenActions.VoteSelected -> rating.onVoteSelected(action.isPositive)
            is HomeScreenActions.IssueToggled -> rating.onIssueToggled(action.issue)
            HomeScreenActions.SubmitClicked -> rating.onSubmitClick()
            is HomeScreenActions.RatingPositionChanged -> rating.onPositionChanged(action.latitude, action.longitude, action.source)
            is HomeScreenActions.RatingNetworkSelected -> rating.onNetworkSelected(action.network)
            HomeScreenActions.ToastShown -> rating.onToastShown()
            HomeScreenActions.RatingDialogDismissed -> rating.onDialogDismissed()
        }
    }

    private fun watchStation(
        station: BikeStation,
        bikeType: BikeType,
    ) = stationWatches.watch(station, bikeType)

    private fun cancelStationWatch(
        station: BikeStation,
        bikeType: BikeType,
    ) = stationWatches.cancel(station, bikeType)

    private fun reloadStationWatches(reportExpired: Boolean = false) = stationWatches.reload(reportExpired)

    private fun onStationNotificationOpened(target: StationNotificationTarget) {
        analytics.track(AnalyticsEvents.stationNotificationOpened())
        onStationNotificationSelected(target)
    }

    private fun onStationNotificationSelected(target: StationNotificationTarget) {
        val existingStation = _stateFlow.value.stationsByNetwork[target.networkId]
            ?.firstOrNull { it.id == target.stationId }
        val immediateStation = target.station ?: existingStation
        _stateFlow.update { state ->
            state.copy(
                stationsByNetwork = immediateStation?.let {
                    state.stationsByNetwork.upsert(target.networkId, it)
                } ?: state.stationsByNetwork,
                mapLayerMode = MapLayerMode.Stations,
                stationFocusTarget = StationFocusTarget(target.networkId, target.stationId),
            )
        }
        scope.launch {
            val stations = catchingRequest { networkRepository.fetchStations(target.networkId) }
                .getOrNull()
                ?.value
                ?: return@launch
            _stateFlow.update { state ->
                val targetedStations = if (stations.any { it.id == target.stationId } || immediateStation == null) {
                    stations
                } else {
                    stations + immediateStation
                }
                state.copy(
                    stationsByNetwork = state.stationsByNetwork + (target.networkId to targetedStations),
                )
            }
        }
    }

    /**
     * A station link was opened (cold start, new Android intent or a browser history step).
     *
     * Goes through the same path as a notification tap: the station may belong to a network that
     * is not loaded yet, so the screen selects it once it resolves rather than this selecting a
     * station that may not exist.
     */
    private fun onStationLinkOpened(target: StationFocusTarget) {
        analytics.track(AnalyticsEvents.stationLinkOpened())
        onStationNotificationSelected(StationNotificationTarget(target.networkId, target.stationId))
    }

    /**
     * Opens [target]'s sheet, replacing the one already open so the stack never grows past it.
     * Settings closes because a notification or link can land here, and the sheet would open unseen.
     */
    private fun onStationSelected(target: StationFocusTarget) {
        analytics.track(AnalyticsEvents.stationSelected())
        _stateFlow.update { it.copy(backStack = listOf(MapRoute, StationRoute(target)), settingsPage = null) }
    }

    /** Closes the open station sheet, if any. */
    private fun onStationDismissed() {
        _stateFlow.update { if (it.backStack.size == 1) it else it.copy(backStack = listOf(MapRoute)) }
    }

    private fun requestDebugFoundNotification() {
        scope.launch {
            val stations = catchingRequest { networkRepository.fetchStations(BicingNetworkId) }
                .getOrNull()
                ?.value
                ?: return@launch
            val station = stations.firstOrNull { it.normalBikes > 0 }
                ?: stations.firstOrNull { it.electricBikes > 0 }
                ?: return@launch
            _stateFlow.update {
                it.copy(stationsByNetwork = it.stationsByNetwork + (BicingNetworkId to stations))
            }
            val watch = StationWatch(
                networkId = station.networkId,
                stationId = station.id,
                stationName = station.name,
                bikeType = if (station.normalBikes > 0) BikeType.Normal else BikeType.Electric,
                expiresAtEpochMillis = Long.MAX_VALUE,
                station = station,
            )
            stationWatches.report(StationWatchEvent.Found(listOf(watch)))
        }
    }

    private fun consumeStationWatchEvents(count: Int) {
        _stateFlow.update { it.copy(pendingStationWatchEvents = it.pendingStationWatchEvents.drop(count)) }
    }

    private fun onStationFocusHandled(target: StationFocusTarget) {
        _stateFlow.update {
            if (it.stationFocusTarget == target) it.copy(stationFocusTarget = null) else it
        }
    }

    private fun refreshWatchedStations() {
        if (_stateFlow.value.stationWatches.isEmpty()) return
        scope.launch { refreshStations(maxAge = Duration.ZERO, includeVisible = false) }
    }

    private fun retryLoadNetworks() {
        scope.launch { loadNetworks() }
    }

    private fun onThemeModeSelected(mode: ThemeMode) {
        themePreferences.setThemeMode(mode)
        _stateFlow.update { it.copy(themeMode = mode) }
        analytics.track(AnalyticsEvents.settingChanged("theme", mode.analyticsValue()))
    }

    private fun onLanguageSelected(language: LanguagePreference) {
        languagePreferences.setLanguagePreference(language)
        preferredLanguageTag = language.tag
        _stateFlow.update { it.copy(language = language) }
        analytics.track(AnalyticsEvents.settingChanged("language", language.tag ?: "system"))
    }

    private fun onSettingsPageOpened(page: SettingsPage) {
        _stateFlow.update { it.copy(settingsPage = page) }
    }

    /** One page up, or closed from the top page. */
    private fun onSettingsBack() {
        _stateFlow.update { it.copy(settingsPage = it.settingsPage?.parent) }
    }

    private fun onSettingsClosed() {
        _stateFlow.update { it.copy(settingsPage = null) }
    }

    private fun onInstallPromptDismissed() {
        _stateFlow.update { it.copy(installPromptDismissed = true) }
    }

    /**
     * Puts the map on the layer that was tapped — live docking stations or OSM bike racks.
     * Tapping the layer already showing does nothing, rather than flipping to the other one.
     *
     * Switching to racks also loads them for whatever is on screen, since unlike stations they
     * are not fetched in the background — there is nothing live about a rack to keep fresh, so
     * they are only worth a trip when somebody asks to see them.
     *
     * The choice is persisted so the next launch opens on the same layer. A station opened from a
     * notification or link still forces the stations layer for that session without overwriting it.
     *
     * Visible-station polling idles while the racks are up. Networks with active watches remain
     * in the refresh cycle because their availability is still user-visible through alerts.
     */
    private fun onMapLayerModeSelected(mode: MapLayerMode) {
        if (_stateFlow.value.mapLayerMode == mode) return
        mapLayerPreferences.setMapLayerMode(mode)
        _stateFlow.update { it.copy(mapLayerMode = mode) }
        analytics.track(AnalyticsEvents.settingChanged("map_layer", mode.analyticsValue()))
        // The pill belongs to the stations layer, so it goes and comes back with this tap rather
        // than whenever the countdown loop next happens to look.
        publishRefreshStatus()
        publishStationsLoading()
        if (mode == MapLayerMode.Parking) scope.launch { parkingRacks.loadForVisible() }
    }

    private fun retryLoadParking() = parkingRacks.retry()

    /**
     * Tells the model what the camera is currently looking at, nearest network first.
     *
     * Anything that hasn't been fetched recently loads straight away, so panning onto a new
     * city doesn't wait out a whole refresh interval before showing anything.
     */
    private fun onVisibleNetworksChanged(
        networkIds: List<String>,
        zoom: Double,
        visibleBounds: BoundingBox? = null,
    ) {
        visibleNetworkIds = networkIds.take(refreshBudgetAt(zoom))
        // Zoomed out, the map draws networks rather than stations, so an empty list there only
        // means nothing is polled. And with no networks loaded yet, nothing could be in view.
        val noNetworkInView = zoom >= STATIONS_VISIBLE_ZOOM &&
            networkIds.isEmpty() &&
            _stateFlow.value.networks.isNotEmpty()
        _stateFlow.update { it.copy(noNetworkInView = noNetworkInView) }
        // What is on screen is half of whether there is a countdown to show, so the pill appears
        // with the first networks rather than up to a second after them.
        publishRefreshStatus()
        publishStationsLoading()
        currentZoom = zoom
        parkingRacks.onCameraMoved(zoom, visibleBounds)
        scope.launch {
            if (isDrawingStations) {
                refreshVisible(maxAge = RefreshInterval)
            } else {
                parkingRacks.loadForVisible()
            }
        }
    }

    /**
     * Whether stations are the layer on screen, and so the only thing worth spending requests on.
     *
     * Read from state rather than tracked separately: the toggle is the one writer, and a second
     * copy of it here is a second thing to keep in step.
     */
    private val isDrawingStations: Boolean
        get() = _stateFlow.value.mapLayerMode == MapLayerMode.Stations

    /**
     * Starts the polling loop. Safe to call when it is already running.
     *
     * Resumes the existing deadline if one is still ahead, rather than always handing out a
     * fresh 30s — see [nextRefreshDeadline].
     */
    private fun startAutoRefresh() {
        if (refreshJob?.isActive == true) return
        refreshJob = scope.launch {
            while (true) {
                val deadline = nextRefreshDeadline ?: (TimeSource.Monotonic.markNow() + RefreshInterval)
                nextRefreshDeadline = deadline
                countDownToNextRefresh(deadline)
                nextRefreshDeadline = null
                _stateFlow.update { it.copy(refreshStatus = RefreshStatus.Refreshing) }
                // The toast that reports this failure goes away by itself and has no retry action.
                if (_stateFlow.value.networksLoadFailed) retryLoadNetworks()
                // Below RefreshZoomThreshold, a network that's already loaded keeps whatever it
                // has instead of being re-fetched every cycle — see RefreshZoomThreshold's doc.
                val maxAge = if (currentZoom >= RefreshZoomThreshold) Duration.ZERO else Duration.INFINITE
                refreshVisible(maxAge = maxAge)
            }
        }
        pendingVoteRecovery.start()
        stationWatches.startExpiryTimer()
    }

    private fun stopAutoRefresh() {
        refreshJob?.cancel()
        refreshJob = null
        _stateFlow.update { it.copy(refreshStatus = RefreshStatus.Idle) }
        pendingVoteRecovery.stop()
        stationWatches.stopExpiryTimer()
    }

    /**
     * Waits out the given deadline, publishing the seconds left as it goes.
     *
     * Counted against a fixed deadline rather than by subtracting a second at a time, so the
     * displayed countdown can't drift away from when the request actually fires. The deadline
     * is passed in rather than computed here so a countdown interrupted by the screen going to
     * the background can be resumed instead of restarted — see [nextRefreshDeadline].
     */
    private suspend fun countDownToNextRefresh(deadline: TimeMark) {
        while (true) {
            val remaining = -deadline.elapsedNow()
            if (remaining <= Duration.ZERO) return
            publishRefreshStatus()
            delay(minOf(remaining, OneSecond))
        }
    }

    /**
     * Publishes what the countdown pill should currently say.
     *
     * Also called by whatever changes *whether* there is a countdown worth showing, not just by
     * the loop: the loop only comes back around once a second, so a pill left to it alone
     * appeared up to a second after the tap that should have brought it back — long enough to
     * read as the switch having done nothing.
     */
    private fun publishRefreshStatus() {
        // The loop is what the pill is reporting on; with it stopped there is no next update to
        // promise, whatever the deadline it was interrupted mid-wait with still says.
        if (refreshJob?.isActive != true) return
        val remaining = nextRefreshDeadline?.let { -it.elapsedNow() }
        val status = when {
            // Nothing on screen to refresh means nothing worth counting down to — including
            // while the racks are up, where the loop keeps its cadence but refreshes nothing.
            visibleNetworkIds.isEmpty() || !isDrawingStations -> RefreshStatus.Idle

            // No deadline left to wait on: the loop is between countdowns, mid-request.
            remaining == null || remaining <= Duration.ZERO -> RefreshStatus.Refreshing

            // Rounded up to whole seconds, so e.g. 29.4s reads "30s" and the countdown
            // never starts one second above the interval it's counting down from.
            else -> RefreshStatus.Waiting(remaining.inWholeMilliseconds.ceilToSeconds())
        }
        _stateFlow.update { it.copy(refreshStatus = status) }
    }

    private suspend fun loadNetworks() {
        // The repository falls back to its cache before it gives up, so reaching `onFailure`
        // means a first run with no connection and nothing stored — the one case where there
        // is genuinely nothing to draw and the refresh loop keeps retrying.
        catchingRequest { networkRepository.fetchNetworks() }
            .onSuccess { networks ->
                _stateFlow.update {
                    it.copy(
                        networks = networks,
                        networksLoadFailed = false,
                        // Only the first load refines the guess; by a later one the map has moved on.
                        startPosition = if (it.networks.isEmpty()) {
                            guessStartPosition(networks) ?: it.startPosition
                        } else {
                            it.startPosition
                        },
                    )
                }
            }.onFailure {
                _stateFlow.update { it.copy(networksLoadFailed = true) }
            }
    }

    /**
     * Refetches visible networks and every network with an active station watch.
     *
     * The visible set is read here rather than captured by the caller, so the polling loop
     * keeps a steady cadence while the user pans instead of being restarted by every move.
     */
    private suspend fun refreshVisible(maxAge: Duration) = refreshStations(maxAge, includeVisible = true)

    private suspend fun refreshStations(
        maxAge: Duration,
        includeVisible: Boolean,
    ) = coroutineScope {
        stationWatches.expireNow()
        val watchedNetworkIds = stationWatches.watchedNetworkIds()
        val targets = buildList {
            if (includeVisible && isDrawingStations) addAll(visibleNetworkIds)
            addAll(watchedNetworkIds)
        }.distinct()
        if (targets.isEmpty()) return@coroutineScope
        val outcomesByNetwork = targets
            .map { networkId ->
                async {
                    val targetMaxAge = if (networkId in watchedNetworkIds) Duration.ZERO else maxAge
                    networkId to loadStations(networkId, targetMaxAge)
                }
            }.awaitAll()
        val refreshedNetworkIds = outcomesByNetwork
            .filter { (_, outcome) -> outcome == LoadOutcome.Fresh }
            .mapTo(mutableSetOf()) { it.first }
        if (refreshedNetworkIds.isNotEmpty()) stationWatches.evaluate(refreshedNetworkIds)
        val visibleTargets = visibleNetworkIds.toSet()
        val outcomes = outcomesByNetwork
            .filter { (networkId) -> networkId in visibleTargets }
            .map { it.second }
            // Networks skipped for being fresh enough, or already in flight, say nothing
            // either way about whether the connection is working.
            .filterNotNull()
        if (outcomes.isEmpty()) return@coroutineScope
        // One network answering is enough to call the cycle good: with several on screen, a
        // single operator's feed being down is its problem rather than the user's connection.
        val isStale = outcomes.none { it == LoadOutcome.Fresh }
        // Only worth surfacing once the whole cycle has actually failed — a rate limit on one
        // network while others are still answering isn't the story to tell the rider.
        val isRateLimited = isStale && outcomes.any { it == LoadOutcome.RateLimited }
        _stateFlow.update {
            it.copy(freshness = it.freshness.copy(isStale = isStale, isRateLimited = isRateLimited))
        }
    }

    private suspend fun loadStations(
        networkId: String,
        maxAge: Duration,
    ): LoadOutcome? {
        if (loadedAt[networkId]?.elapsedNow()?.let { it < maxAge } == true) return null
        // A network slower to answer than the refresh interval must not stack up requests.
        if (!inFlight.add(networkId)) return null
        publishStationsLoading()
        try {
            val result = catchingRequest { networkRepository.fetchStations(networkId) }
            // Nothing cached and nothing reachable — leave whatever is on screen alone and
            // say the cycle didn't land.
            val snapshot = result.getOrNull()
                ?: return if (result.exceptionOrNull() is RateLimitedException) {
                    LoadOutcome.RateLimited
                } else {
                    LoadOutcome.Stale
                }
            val previous = _stateFlow.value.stationsByNetwork[networkId]
            // Nothing to compare against on the very first load, and flagging every station
            // at once would say nothing anyway.
            val now = TimeSource.Monotonic.markNow()
            val changed = previous?.let { findChangedStations(it, snapshot.value) }.orEmpty()
            _stateFlow.update {
                it.copy(
                    stationsByNetwork = it.stationsByNetwork + (networkId to snapshot.value),
                    changedAt = it.changedAt + changed.associateWith { now },
                )
            }
            recordUpdate(snapshot.updatedAtEpochMillis)
            if (snapshot.isStale) {
                // Served off disk. `loadedAt` deliberately stays unset, so the next cycle
                // tries again instead of treating cached numbers as recently fetched.
                return if (snapshot.isRateLimited) LoadOutcome.RateLimited else LoadOutcome.Stale
            }
            loadedAt[networkId] = TimeSource.Monotonic.markNow()
            return LoadOutcome.Fresh
        } finally {
            inFlight.remove(networkId)
            publishStationsLoading()
        }
    }

    private fun publishStationsLoading() {
        val state = _stateFlow.value
        val loading = isDrawingStations &&
            visibleNetworkIds.any { it in inFlight && it !in state.stationsByNetwork }
        _stateFlow.update { if (it.isLoadingStations == loading) it else it.copy(isLoadingStations = loading) }
    }

    /**
     * Keeps the newest timestamp across the visible networks.
     *
     * Never moves backwards: a cached network answering after a live one would otherwise
     * make the screen claim data older than what it is actually showing.
     */
    private fun recordUpdate(updatedAtEpochMillis: Long) {
        _stateFlow.update {
            val known = it.freshness.updatedAtEpochMillis
            if (known == null || updatedAtEpochMillis > known) {
                it.copy(freshness = it.freshness.copy(updatedAtEpochMillis = updatedAtEpochMillis))
            } else {
                it
            }
        }
    }

    data class State(
        val networks: List<BikeNetwork> = emptyList(),
        val networksLoadFailed: Boolean = false,
        /** Whether the map is drawing docking stations or OSM bike racks. See [MapLayerMode]. */
        val mapLayerMode: MapLayerMode = MapLayerMode.Stations,
        /**
         * Bike racks, by the fixed patch of ground they were fetched for.
         *
         * Keyed by [ParkingCell] rather than by network: racks are OpenStreetMap's, not any
         * operator's, and they exist in places no bike-share network reaches. Cells are fixed, so
         * ground already loaded is recognised rather than worked out again.
         */
        val parkingByCell: Map<ParkingCell, List<BikeParkingSpot>> = emptyMap(),
        /**
         * The same racks grouped into counted blobs, for the zooms too far out to draw them singly.
         *
         * Held rather than derived in the draw phase because it is O(racks) and the draw phase runs
         * on every frame of a drag — see `clusterParkingSpots`.
         */
        val parkingAreas: List<ParkingAreaSummary> = emptyList(),
        /**
         * True while a visible network is being fetched for the first time, so the screen can say
         * why no stations are drawn there yet. Later refreshes of a network already on screen
         * leave it false: the countdown pill covers those.
         */
        val isLoadingStations: Boolean = false,
        /**
         * True when the map is close enough to show stations but no bike-share network reaches
         * the area, so the screen can say so instead of drawing an empty map.
         */
        val noNetworkInView: Boolean = false,
        /** Where the map opens, guessed without asking for location. Null leaves it on Barcelona. */
        val startPosition: StartPosition? = null,
        /** True while a rack area is in flight, so the screen can say why the map is still empty. */
        val isLoadingParking: Boolean = false,
        /**
         * True when a round of rack requests came back with nothing at all.
         *
         * Failures used to show as the spinner simply disappearing, which reads as "there are no
         * racks here" — the one thing a failed Overpass request must never be mistaken for.
         */
        val parkingLoadFailed: Boolean = false,
        val stationsByNetwork: Map<String, List<BikeStation>> = emptyMap(),
        /**
         * When each station's numbers last moved, for the map to flag briefly.
         *
         * Marks rather than a plain set of ids: a refresh that changes nothing must not re-flash
         * what the previous one did, so the map needs to know how old each change is. Entries are
         * left to go stale instead of being swept — there is at most one per station.
         */
        val changedAt: Map<String, ComparableTimeMark> = emptyMap(),
        /** Where the refresh cycle currently stands, for the screen to show. */
        val refreshStatus: RefreshStatus = RefreshStatus.Idle,
        /** How old the displayed availability is, so the screen can admit when it is behind. */
        val freshness: DataFreshness = DataFreshness(),
        val ratingUiState: BikeRatingUiState = BikeRatingUiState(),
        /** How long until a just-parked Bicing bike can be swapped for another. See [ParkingCountdownStatus]. */
        val parkingCountdownStatus: ParkingCountdownStatus = ParkingCountdownStatus.Idle,
        val stationWatches: List<StationWatch> = emptyList(),
        val stationFocusTarget: StationFocusTarget? = null,
        /**
         * Where the rider is: the map, plus a [StationRoute] on top while a station's sheet is open.
         *
         * The single source of truth for which station is selected. Hosts mirror it to the address
         * bar and feed links back through [HomeViewModel.onStationLinkOpened], so it has to live
         * here and not in the composition.
         */
        val backStack: List<HomeRoute> = listOf(MapRoute),
        val themeMode: ThemeMode = ThemeMode.Auto,
        val language: LanguagePreference = LanguagePreference.System,
        val installPromptDismissed: Boolean = false,
        /**
         * The open settings page, if any. Deliberately not on [backStack]: the browser mirrors that
         * stack, so a route on top of a station would read as the station closing and step history back.
         */
        val settingsPage: SettingsPage? = null,
        /**
         * Watch outcomes the host has not yet been told about, oldest first.
         *
         * State rather than an event stream: the screen hands them to the host and then consumes the
         * count it handled, so one raised while nothing was collecting waits instead of vanishing.
         */
        val pendingStationWatchEvents: List<StationWatchEvent> = emptyList(),
    ) {
        /** The station whose sheet is open, if any. */
        val selectedStationTarget: StationFocusTarget?
            get() = (backStack.lastOrNull() as? StationRoute)?.target
    }
}
